<script setup lang="ts">
import { router } from '@inertiajs/vue3'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import DatePicker from 'primevue/datepicker'
import Textarea from 'primevue/textarea'
import { ref, watch } from 'vue'
import Button from '@/Components/Utils/Button.vue'
import Icon from '@/Components/Utils/Icon.vue'
import LoadingIcon from '@/Components/Utils/LoadingIcon.vue'
import { useFormatTime } from '@/Composables/useFormatTime'
import { debounce } from 'lodash-es'
import axios from 'axios'
import { notify } from '@kyvg/vue3-notification'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faSearch, faCheck, faTimes } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
import InformationIcon from '@/Components/Utils/InformationIcon.vue'
library.add(faSearch, faCheck, faTimes)

const isOpenModal = ref(false)

// -- Order search
const searchQuery = ref('')
const isLoadingOrder = ref(false)
const listOrder = ref<any[]>([])

const fetchOrder = async (q = '') => {
    isLoadingOrder.value = true
    try {
        const { data } = await axios.get(route('json.orders.finished'), { params: { q } })
        listOrder.value = data.data ?? data
    } catch {
        notify({ title: 'Oops!', text: 'Gagal mencari data Order', type: 'error' })
    } finally {
        isLoadingOrder.value = false
    }
}
const debFetchOrder = debounce((q: string) => fetchOrder(q), 350)
watch(searchQuery, (val) => debFetchOrder(val))

const selectedOrderIds = ref<number[]>([])
const toggleOrder = (id: number) => {
    const idx = selectedOrderIds.value.indexOf(id)
    if (idx === -1) selectedOrderIds.value.push(id)
    else selectedOrderIds.value.splice(idx, 1)
}

// -- Customer search
const customerQuery = ref('')
const isLoadingCustomer = ref(false)
const listCustomer = ref<any[]>([])
const selectedCustomerId = ref<number | null>(null)

const fetchCustomers = async (q = '') => {
    isLoadingCustomer.value = true
    try {
        const { data } = await axios.get(route('json.customers'), { params: { q } })
        listCustomer.value = data.data ?? data
    } catch {
        notify({ title: 'Oops!', text: 'Gagal mencari pelanggan', type: 'error' })
    } finally {
        isLoadingCustomer.value = false
    }
}
const debFetchCustomer = debounce((q: string) => fetchCustomers(q), 350)
watch(customerQuery, (val) => {
    if (val) debFetchCustomer(val)
    else listCustomer.value = []
})

const selectCustomer = (customer: any) => {
    selectedCustomerId.value = customer.id
    recipientName.value = customer.name
    recipientAddress.value = customer.address ?? ''
    recipientPhone.value = customer.phone ?? ''
    customerQuery.value = ''
    listCustomer.value = []
}

const clearCustomer = () => {
    selectedCustomerId.value = null
    recipientName.value = ''
    recipientAddress.value = ''
    recipientPhone.value = ''
}

// -- Recipient fields
const recipientName = ref('')
const recipientAddress = ref('')
const recipientPhone = ref('')

// -- Other fields
const deliveryDate = ref<Date | null>(null)
const note = ref('')

const onOpen = () => {
    selectedOrderIds.value = []
    searchQuery.value = ''
    customerQuery.value = ''
    listCustomer.value = []
    selectedCustomerId.value = null
    recipientName.value = ''
    recipientAddress.value = ''
    recipientPhone.value = ''
    deliveryDate.value = null
    note.value = ''
    fetchOrder()
    isOpenModal.value = true
}

// -- Submit
const isLoadingSubmit = ref(false)
const onSubmit = () => {
    router.post(
        route('casheer.delivery_notes.store'),
        {
            orders:             selectedOrderIds.value,
            customer_id:        selectedCustomerId.value,
            recipient_name:     recipientName.value || null,
            recipient_address:  recipientAddress.value || null,
            recipient_phone:    recipientPhone.value || null,
            delivery_date:      deliveryDate.value ? deliveryDate.value.toISOString() : null,
            note:               note.value || null,
        },
        {
            onStart: () => (isLoadingSubmit.value = true),
            onFinish: () => (isLoadingSubmit.value = false),
            onSuccess: () => {
                selectedOrderIds.value = []
                isOpenModal.value = false
            },
            onError: () => notify({ title: 'Oops!', text: 'Gagal membuat Surat Jalan', type: 'error' }),
        }
    )
}
</script>

<template>
    <div>
        <Button label="Buat Surat Jalan" icon="fas fa-plus" @click="onOpen" />

        <Dialog
            v-model:visible="isOpenModal"
            modal
            header="Buat Surat Jalan Baru"
            :style="{ width: '36rem' }"
        >
            <!-- Order search -->
            <div class="mb-3">
                <label class="block text-sm font-medium mb-1">
                    Cari Order (selesai produksi)
                    <InformationIcon information="Hanya dapat memilih order yang sudah selesai produksi dan belum masuk di Surat Jalan lain" />
                </label>
                <span class="p-input-icon-left w-full relative">
                    <FontAwesomeIcon icon="fal fa-search" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <InputText
                        v-model="searchQuery"
                        placeholder="No. order, nama pelanggan, hari, bulan, tahun…"
                        class="w-full !pl-9"
                    />
                </span>
            </div>

            <!-- Order list -->
            <div class="border border-gray-200 rounded-md overflow-y-auto max-h-48 min-h-14 mb-1">
                <div v-if="isLoadingOrder" class="flex items-center justify-center h-14 text-gray-400">
                    <LoadingIcon />
                </div>
                <div v-else-if="!listOrder.length" class="flex items-center justify-center h-14 text-gray-400 text-sm italic">
                    Tidak ada order yang tersedia
                </div>
                <ul v-else class="divide-y divide-gray-100">
                    <li
                        v-for="order in listOrder"
                        :key="order.id"
                        class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 select-none"
                        :class="selectedOrderIds.includes(order.id) ? 'bg-sky-50' : ''"
                        @click="toggleOrder(order.id)"
                    >
                        <div
                            class="w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors"
                            :class="selectedOrderIds.includes(order.id) ? 'bg-sky-500 border-sky-500 text-white' : 'border-gray-300'"
                        >
                            <FontAwesomeIcon v-if="selectedOrderIds.includes(order.id)" icon="fal fa-check" class="text-xs" />
                        </div>
                        <div class="flex-1 min-w-0">
                            <div class="flex items-center gap-2">
                                <span class="font-semibold text-sm">{{ order.reference }}</span>
                                <Icon v-if="order.type_icon" :data="order.type_icon" class="text-xs" />
                                <Icon v-if="order.state_icon" :data="order.state_icon" class="text-xs" />
                            </div>
                            <div class="text-xs text-gray-500 truncate">
                                <span>{{ order.customer?.name ?? 'Tanpa pelanggan' }}</span>
                                <span class="mx-1">·</span>
                                <span>{{ order.product_name ?? '-' }}</span>
                                <span class="mx-1">·</span>
                                <span>Qty: {{ order.quantity }}</span>
                                <span class="mx-1">·</span>
                                <span>{{ useFormatTime(order.created_at) }}</span>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
            <p class="mb-4 text-xs text-gray-500">{{ selectedOrderIds.length }} order dipilih</p>

            <hr class="my-3 border-gray-200" />

            <!-- Recipient section -->
            <div class="mb-3">
                <div class="flex items-center justify-between mb-2">
                    <label class="block text-sm font-medium">Penerima <span class="text-gray-400 font-normal">(opsional)</span></label>
                    <button
                        v-if="selectedCustomerId || recipientName"
                        type="button"
                        class="text-xs text-red-400 hover:text-red-600"
                        @click="clearCustomer"
                    >
                        <FontAwesomeIcon icon="fal fa-times" class="mr-1" />Hapus penerima
                    </button>
                </div>

                <!-- Customer search (only shown when not yet selected) -->
                <div v-if="!selectedCustomerId" class="mb-2 relative">
                    <span class="p-input-icon-left w-full relative">
                        <FontAwesomeIcon icon="fal fa-search" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <InputText
                            v-model="customerQuery"
                            placeholder="Cari pelanggan untuk auto-isi…"
                            class="w-full !pl-9"
                        />
                    </span>
                    <div v-if="isLoadingCustomer" class="absolute z-10 w-full bg-white border border-gray-200 rounded-md shadow mt-1 flex items-center justify-center py-2">
                        <LoadingIcon />
                    </div>
                    <ul v-else-if="listCustomer.length" class="absolute z-10 w-full bg-white border border-gray-200 rounded-md shadow mt-1 max-h-40 overflow-y-auto">
                        <li
                            v-for="c in listCustomer"
                            :key="c.id"
                            class="px-3 py-2 cursor-pointer hover:bg-sky-50 text-sm"
                            @click="selectCustomer(c)"
                        >
                            <span class="font-semibold">{{ c.name }}</span>
                            <span v-if="c.phone" class="ml-2 text-gray-400 text-xs">{{ c.phone }}</span>
                        </li>
                    </ul>
                </div>

                <!-- Editable recipient fields -->
                <div class="space-y-2">
                    <InputText v-model="recipientName" placeholder="Nama penerima" class="w-full" />
                    <InputText v-model="recipientPhone" placeholder="No. HP penerima" class="w-full" />
                    <Textarea v-model="recipientAddress" rows="2" class="w-full" placeholder="Alamat penerima" />
                </div>
            </div>

            <hr class="my-3 border-gray-200" />

            <!-- Delivery date & note -->
            <div class="space-y-3">
                <div>
                    <div class="flex items-center justify-between mb-1">
                        <label class="block text-sm font-medium">Tanggal Pengiriman <span class="text-gray-400 font-normal">(opsional)</span></label>
                        <button v-tooltip="'Klik untuk mengisi tanggal pengiriman sesuai waktu saat ini'" type="button" class="text-xs text-sky-500 hover:text-sky-700 font-medium underline" @click="deliveryDate = new Date()">Sekarang</button>
                    </div>
                    <DatePicker v-model="deliveryDate" showTime showIcon class="w-full" dateFormat="dd/mm/yy" />
                </div>
                <div>
                    <label class="block text-sm font-medium mb-1">
                        Catatan <span class="text-gray-400 font-normal">(opsional)</span>
                        <InformationIcon information="Catatan ini akan ditampilkan di Surat Jalan dan dapat dilihat driver" class="ml-2" />
                    </label>
                    <Textarea v-model="note" rows="2" class="w-full" placeholder="Catatan pengiriman…" />
                </div>
            </div>

            <div class="flex justify-end gap-2 mt-4">
                <Button label="Batal" type="cancel" @click="isOpenModal = false" />
                <Button
                    label="Buat Surat Jalan"
                    full
                    :disabled="!selectedOrderIds.length"
                    :loading="isLoadingSubmit"
                    @click="onSubmit"
                />
            </div>
        </Dialog>
    </div>
</template>